/**
 * Portal REST client (ADR-0010 P6/P7 · `/api/portal`) — the end-user
 * surface, distinct from the operator `api` (`lib/api.ts`). Carries the
 * portal JWT from `usePortalAuth`; a 401 clears the portal session only.
 */
import { ApiError } from "./api";
import type { JsLibraryOut } from "./api-apps";
import type { PageAst } from "./pageTypes";
import type { VoicePreview, VoiceToolRows } from "./portalVoice";
import { usePortalAuth, type PortalUser } from "./portalAuth";
import { shouldRefresh, tokenExpiryMs } from "./session";

export type { PageAst, PageNode } from "./pageTypes";

const BASE = "/api/portal";

// Single-flight: concurrent portal calls share one refresh request.
let portalRefreshInFlight: Promise<void> | null = null;

/**
 * Refresh the portal access token before it expires (mirror of
 * `lib/session.ts::maybeRefreshSession` for the `app_user` realm). No-op without a
 * refresh token or while the access token has plenty of life; a rejected refresh logs
 * the session out; a network error is swallowed. Called before every portal request.
 */
export async function maybeRefreshPortalSession(): Promise<void> {
  const { token, refreshToken, user } = usePortalAuth.getState();
  if (!token || !user || !refreshToken) return;
  const exp = tokenExpiryMs(token);
  if (exp === null || !shouldRefresh(exp, Date.now())) return;

  portalRefreshInFlight ??= fetch(new URL(BASE + "/auth/refresh", window.location.origin), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh_token: refreshToken }),
  })
    .then(async (r) => {
      const state = usePortalAuth.getState();
      if (state.refreshToken !== refreshToken || !state.user) return; // a logout raced us
      if (r.ok) {
        const data = (await r.json()) as { access_token?: string; refresh_token?: string };
        if (data.access_token && data.refresh_token) {
          state.setSession(data.access_token, state.user, data.refresh_token);
        }
      } else if (r.status === 401 || r.status === 403) {
        state.logout();
      }
    })
    .catch(() => undefined)
    .finally(() => {
      portalRefreshInFlight = null;
    });
  await portalRefreshInFlight;
}

/** A portal inbox notification (ADR-0020 P3, apps-plane). */
export interface PortalNotification {
  id: string;
  kind: string;
  severity: string;
  title: string;
  body: string | null;
  link: string | null;
  payload: Record<string, unknown> | null;
  occurrence_count: number;
  read: boolean;
  created_at: string | null;
  last_occurred_at: string | null;
}

export interface PortalNotificationsPage {
  notifications: PortalNotification[];
  total: number;
  unread: number;
}

/** A persisted assistant conversation thread (ADR-0028 — server-side memory). */
export interface AssistantSessionSummary {
  id: string;
  title: string | null;
  surface: string;
  message_count: number;
  created_at: string;
  updated_at: string;
}

/** Where a message sits among its SIBLINGS (ADR-0072) — drives the `‹ 2/3 ›`
 *  switcher. `total === 1` (the common case) means there's no fork here. */
export interface MessageBranch {
  index: number;
  total: number;
  /** The sibling ids, in order — switching branch is `setAssistantActiveLeaf(siblings[i])`
   *  (the server descends to that branch's tip), so no discovery round-trip. */
  siblings: string[];
}

/** One persisted message on the thread's ACTIVE PATH (ADR-0072 — the transcript is
 *  a tree; this list is the branch being looked at). Assistant rows may carry the
 *  turn's TOOL CARDS (ADR-0071 F1b — name + preview + capped result rows/chart)
 *  so a reopened thread rebuilds the rich transcript, not just the text. */
export interface AssistantSessionMessage {
  /** Fork / edit / regenerate = POST a turn with `parent_id` set to one of these. */
  id: string;
  parent_id: string | null;
  role: "user" | "assistant";
  text: string;
  seq: number;
  branch?: MessageBranch | null;
  tools?: { name?: string; preview?: VoicePreview; result?: VoiceToolRows }[] | null;
}

/** The native-input modalities this app's assistant model accepts (ADR-0034 badge). */
export interface PortalAssistantCapabilities {
  model: string | null;
  provider: string | null;
  /** Subset of `["image","pdf"]`; empty ⇒ text-only. */
  vision: string[];
  configured: boolean;
  /** Whether the resolved assistant can READ uploaded files (the bound agent carries the
   *  `uploads`/`code_exec` capability). The system assistant has none, so the chat HIDES the
   *  attach button when this is false — no capability, no upload affordance (ADR-0034). */
  uploads: boolean;
}

/** One suggested opening question for the empty chat, GROUNDED in a readable surface. */
export interface PortalAssistantStarter {
  /** Short chip label (the surface name). */
  label: string;
  /** The full message sent when the chip is clicked. */
  prompt: string;
}

// ---------------------------------------------------------------------------
// Types (mirror the backend portal response shapes)
// ---------------------------------------------------------------------------

export interface PortalRole {
  id: string;
  slug: string;
  name: string;
}

export interface PortalAuthResult {
  access_token: string;
  token_type: string;
  user: PortalUser;
  role: PortalRole;
}

export interface PortalRowsPage {
  rows: Record<string, unknown>[];
  total: number;
  page: number;
  page_size: number;
  /** Source table id — the data_list row-action delete uses it (S5). Optional
   *  for non-page query results (rows/query endpoint doesn't set it). */
  table_id?: string;
  /** The raw resolved value (ADR-0022): mirrors `rows` for table/dataset, or
   *  carries an operation/static resource's scalar/object as `$queries.<n>.data`. */
  data?: unknown;
}

/** One declared, typed report render param (R2 — the Type provider's
 *  contract, `hub.reports.providers.provider_param_schema`). `options_from`
 *  set (R2b) ⇒ the dialog renders a dropdown fed by `reportParamOptions`
 *  instead of a free-text box. */
export interface PortalReportParam {
  name: string;
  type: string;
  required: boolean;
  description?: string | null;
  /** Opaque source marker (table/dataset/sql/static — resolved server-side);
   *  the dialog only checks its PRESENCE and fetches the options. */
  options_from?: Record<string, unknown> | null;
}

/** One dynamic option of a report render param (R2b). */
export interface PortalReportParamOption {
  value: string;
  label: string;
}

/** A report the member's role is granted (ADR-0060 S5) — what a `print_report`
 *  action can render. `param_schema` drives the end-user params dialog (R2). */
export interface PortalReportInfo {
  id: string;
  name: string;
  description: string | null;
  report_type_id: string;
  report_type_name: string;
  param_schema: PortalReportParam[];
}

/** The value stored in a `file`-type column (S5c) — a descriptor pointing at
 *  a BlobStorage object (uploaded via the portal). */
export interface PortalFileValue {
  ref: string;
  filename: string;
  size: number;
  content_type: string | null;
}

export interface PortalFormField {
  name: string;
  label: string;
  required: boolean;
  widget: string;
  default?: unknown;
  help?: string | null;
  show_when?: Record<string, unknown> | null;
  /** For a `select` widget: the allowed choices (the column's enum domain). */
  options?: string[] | null;
  /** Column kind ("data" | "relation"). */
  kind?: string;
  /** For a `relation` widget: the target table id the picker queries. */
  target_table?: string | null;
  /** Form pro A — the layout section this field belongs to (id), or null for
   *  the implicit default section. */
  section?: string | null;
  /** Form pro A — grid columns this field spans (1 or 2), already clamped to
   *  its section's width by the backend. */
  col_span?: number;
  /** Form pro B — per-field validation rules (pattern / length / numeric
   *  bounds / custom message); evaluated client-side for inline errors. */
  validation?: import("@/lib/formValidation").FieldValidation | null;
  /** For a `relation` widget: the target columns to show in a LOOKUP GRID
   *  popover (Business-Central style). Empty/absent → the plain label dropdown. */
  lookup_columns?: string[] | null;
  /** For a `relation` widget: a DRILL-DOWN (dependent picker). `field` = the
   *  sibling form field whose value narrows this one; `column` = the friendly
   *  column on THIS field's target table to match against it. e.g. versione is a
   *  drill-down of modello: {field:"modello", column:"modello"}. */
  depends_on?: { field: string; column: string } | null;
}

/** Form pro A — a layout section: a heading + a 1/2-column grid grouping a run
 *  of fields. */
export interface PortalFormSection {
  id: string;
  title?: string | null;
  description?: string | null;
  columns: number;
}

export interface PortalFormDef {
  slug: string;
  access: string;
  submit_message: string | null;
  /** Bound table id — the record block edits a row of this table (S5). */
  table_id: string;
  fields: PortalFormField[];
  /** Form pro A — explicit layout sections (empty → a single column stack). */
  sections?: PortalFormSection[];
  /** Form pro A — grid columns for the implicit default section (1 or 2). */
  columns?: number;
}

export interface PortalTableSchema {
  table_id: string;
  /** Editable column specs (same shape as form fields) — the record block
   *  binds to a table directly, no form involved (S5d). */
  columns: PortalFormField[];
}

/** A record-activity comment (D21). `at` is an ISO timestamp; `author` is the
 *  principal string (`app_user:<id>`) used for the own-comment delete gate;
 *  `author_label` is the resolved human name (full_name/email) to display. */
export interface PortalComment {
  id: string;
  author: string;
  author_label?: string;
  body: string;
  mentions: string[];
  at: string | null;
}

export interface PortalPageResult {
  page: { id: string; slug: string; title: string; access: string; ast: PageAst };
  queries: Record<string, PortalRowsPage>;
  user: PortalUser | null;
  role: PortalRole | null;
  /** DECLARED app-wide `$global` variables (WS-4a, `apps.global_state`). Used to
   *  seed `$global` in the builder preview (where there's no live runtime store);
   *  the live portal seeds the real store from these on mount. Optional/absent
   *  on the public payload until the backend includes it. */
  globalVariables?: { name: string; initial?: unknown }[];
  /** The app's external JS libraries (ADR-0046) — runtime-CDN pointers the
   *  renderer loads so `{{ _.sum(...) }}` / a JS Object can use their globals.
   *  App-scoped (same on every page); absent until the backend includes it. */
  jsLibraries?: JsLibraryOut[];
}

// ---------------------------------------------------------------------------
// Fetch wrapper (portal token + portal-only logout on 401)
// ---------------------------------------------------------------------------

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  await maybeRefreshPortalSession();
  const token = usePortalAuth.getState().token;
  const res = await fetch(new URL(BASE + path, window.location.origin), {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init.headers,
    },
  });
  if (!res.ok) {
    let payload: { error?: { code: string; message: string } } = {};
    try {
      payload = await res.json();
    } catch {
      /* not JSON */
    }
    if (res.status === 401) usePortalAuth.getState().logout();
    throw new ApiError(
      res.status,
      payload.error?.message ?? res.statusText,
      payload.error?.code ?? "api_error",
    );
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

export const portalApi = {
  signup: (slug: string, body: { email: string; password: string; full_name?: string }) =>
    request<PortalAuthResult>(`/apps/${slug}/auth/signup`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  login: (slug: string, body: { email: string; password: string }) =>
    request<PortalAuthResult>(`/apps/${slug}/auth/login`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  getPage: (slug: string, pageSlug: string) =>
    request<PortalPageResult>(`/apps/${slug}/pages/${pageSlug}`),

  queryRows: (slug: string, tableId: string, body: Record<string, unknown> = {}) =>
    request<PortalRowsPage>(`/apps/${slug}/tables/${tableId}/rows/query`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // Reactive re-fetch of a named page query with runtime search/sort/pagination
  // (S16b — live data over the FULL dataset). The query's static AST filters +
  // RLS still apply server-side; sort overrides the AST sort when provided.
  queryPageData: (
    slug: string,
    pageSlug: string,
    queryName: string,
    body: {
      search?: string;
      filters?: { column: string; op: string; value: unknown }[];
      sort?: { column: string; descending: boolean }[];
      page?: number;
      page_size?: number;
    } = {},
  ) =>
    request<PortalRowsPage>(`/apps/${slug}/pages/${pageSlug}/queries/${queryName}`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // Reactively re-resolve ONE named page resource with current page-state
  // (ADR-0022 Phase C). Generalises queryPageData to EVERY source kind
  // (operation/http/js/static too). The client evaluates the resource's
  // `{{ }}` params (pageExpr) and posts CONCRETE values; the server merges +
  // re-resolves under RLS. `data` carries the raw value (`$queries.<name>.data`).
  resolveResource: (
    slug: string,
    pageSlug: string,
    resourceName: string,
    body: {
      filters?: { column: string; op: string; value: unknown }[];
      sort?: { column: string; descending: boolean }[];
      page_size?: number;
      inputs?: Record<string, unknown>;
      query?: Record<string, unknown>;
      url?: string;
      headers?: Record<string, unknown>;
      body?: unknown;
    } = {},
  ) =>
    request<PortalRowsPage>(`/apps/${slug}/pages/${pageSlug}/resources/${resourceName}/resolve`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // Distinct values of a column → options for a column filter's multiselect
  // (G1). Capped server-side; `truncated` means "type in the contains box".
  queryPageDistinct: (slug: string, pageSlug: string, queryName: string, column: string) =>
    request<{ values: string[]; truncated: boolean }>(
      `/apps/${slug}/pages/${pageSlug}/queries/${queryName}/distinct`,
      { method: "POST", body: JSON.stringify({ column }) },
    ),

  // Single-row read / create / update / delete (ADR-0010 S4 · S12) — the
  // record block + button server actions; write ops gated by the role's
  // write policy server-side.
  getRow: (slug: string, tableId: string, rowId: string) =>
    request<{ row: Record<string, unknown> }>(`/apps/${slug}/tables/${tableId}/rows/${rowId}`),

  createRow: (slug: string, tableId: string, data: Record<string, unknown>) =>
    request<{ row: Record<string, unknown> }>(`/apps/${slug}/tables/${tableId}/rows`, {
      method: "POST",
      body: JSON.stringify({ data }),
    }),

  updateRow: (slug: string, tableId: string, rowId: string, data: Record<string, unknown>) =>
    request<{ row: Record<string, unknown> }>(`/apps/${slug}/tables/${tableId}/rows/${rowId}`, {
      method: "PATCH",
      body: JSON.stringify({ data }),
    }),

  deleteRow: (slug: string, tableId: string, rowId: string) =>
    request<void>(`/apps/${slug}/tables/${tableId}/rows/${rowId}`, { method: "DELETE" }),

  // Record activity / comments (D21). Member-gated + the row must pass the
  // caller's read RLS server-side; delete is author-only for a portal member.
  listActivity: (slug: string, tableId: string, rowId: string) =>
    request<PortalComment[]>(`/apps/${slug}/tables/${tableId}/rows/${rowId}/activity`),

  addComment: (
    slug: string,
    tableId: string,
    rowId: string,
    body: string,
    mentions: string[] = [],
  ) =>
    request<PortalComment>(`/apps/${slug}/tables/${tableId}/rows/${rowId}/activity`, {
      method: "POST",
      body: JSON.stringify({ body, mentions }),
    }),

  deleteComment: (slug: string, tableId: string, commentId: string) =>
    request<void>(`/apps/${slug}/tables/${tableId}/activity/${commentId}`, {
      method: "DELETE",
    }),

  // Run an app workflow from a page button (S14). Only workflows that opted
  // in with a `button_clicked` trigger are runnable; the list powers the
  // builder's picker, the POST fires one (member + opt-in gated server-side).
  listRunnableWorkflows: (slug: string) =>
    request<{ id: string; name: string }[]>(`/apps/${slug}/workflows`),

  runWorkflow: (slug: string, workflowId: string, payload: Record<string, unknown>) =>
    request<{ status: string }>(`/apps/${slug}/workflows/${workflowId}/run`, {
      method: "POST",
      body: JSON.stringify({ payload }),
    }),

  getForm: (slug: string, formSlug: string) =>
    request<PortalFormDef>(`/apps/${slug}/forms/${formSlug}`),

  // Editable column spec for a table (S5d) — the record block binds to a
  // table directly (no form), building its inputs from these columns.
  getTableSchema: (slug: string, tableId: string) =>
    request<PortalTableSchema>(`/apps/${slug}/tables/${tableId}/schema`),

  // Relation-picker options for a target table (RLS-applied): `[{id, label}]`.
  // Pass `columns` (friendly names) for a LOOKUP grid picker (Business-Central
  // style) — each option then carries a `cells` dict for those columns. Pass
  // `filter` ({column, value}) for a DRILL-DOWN (dependent picker) — only rows
  // whose `column` equals `value` (e.g. the versions of the selected model).
  tableOptions: (
    slug: string,
    tableId: string,
    search?: string,
    columns?: string[],
    filter?: { column: string; value: string },
  ) => {
    const qs = new URLSearchParams();
    if (search) qs.set("search", search);
    if (columns && columns.length) qs.set("columns", columns.join(","));
    if (filter && filter.column && filter.value) {
      qs.set("filter_column", filter.column);
      qs.set("filter_value", filter.value);
    }
    const q = qs.toString();
    return request<{ id: string; label: string; cells?: Record<string, unknown> }[]>(
      `/apps/${slug}/tables/${tableId}/options${q ? `?${q}` : ""}`,
    );
  },

  // File attachments for a portal `file` field (S5c). Upload uses multipart
  // (no JSON content-type); download fetches the bytes (member-gated) and
  // triggers a client-side save.
  uploadAttachment: async (
    slug: string,
    formSlug: string,
    file: File,
  ): Promise<PortalFileValue> => {
    const token = usePortalAuth.getState().token;
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetch(
      new URL(`${BASE}/apps/${slug}/forms/${formSlug}/attachments`, window.location.origin),
      { method: "POST", body: fd, headers: token ? { Authorization: `Bearer ${token}` } : {} },
    );
    if (!res.ok) {
      if (res.status === 401) usePortalAuth.getState().logout();
      throw new ApiError(res.status, res.statusText, "upload_failed");
    }
    return res.json() as Promise<PortalFileValue>;
  },
  // Form-less upload (S5d) — the record block edits a table directly, so its
  // `file` fields upload through the app (member-gated), not a form.
  uploadAppAttachment: async (slug: string, file: File): Promise<PortalFileValue> => {
    const token = usePortalAuth.getState().token;
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetch(new URL(`${BASE}/apps/${slug}/attachments`, window.location.origin), {
      method: "POST",
      body: fd,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      if (res.status === 401) usePortalAuth.getState().logout();
      throw new ApiError(res.status, res.statusText, "upload_failed");
    }
    return res.json() as Promise<PortalFileValue>;
  },
  /** The reports the member's role is granted (ADR-0060 S5) + each Type
   *  provider's declared param contract (R2 — drives the print-params dialog). */
  listReports: (slug: string) => request<PortalReportInfo[]>(`/apps/${slug}/reports`),
  /** The LIVE options of a declared render param (R2b — the dialog's dropdown
   *  feed). Grant-gated exactly like the render. */
  reportParamOptions: (slug: string, reportId: string, param: string) =>
    request<{ options: PortalReportParamOption[] }>(
      `/apps/${slug}/reports/${reportId}/param-options?param=${encodeURIComponent(param)}`,
    ),
  /** Render + download a GRANTED report as PDF (ADR-0060 S5). The server
   *  re-checks the member's role grant (link ∩ allow-list) — an ungranted
   *  report is a 404. Params feed the Type's provider server-side. */
  printReport: async (slug: string, reportId: string, params: Record<string, unknown>) => {
    const token = usePortalAuth.getState().token;
    const { triggerBlobDownload } = await import("./api-workflows");
    const res = await fetch(
      new URL(`${BASE}/apps/${slug}/reports/${reportId}/render`, window.location.origin),
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ params, format: "pdf" }),
      },
    );
    if (!res.ok) throw new ApiError(res.status, res.statusText, "report_render_failed");
    const cd = res.headers.get("Content-Disposition") ?? "";
    const name = /filename="([^"]+)"/.exec(cd)?.[1] ?? "report.pdf";
    triggerBlobDownload(await res.blob(), name);
  },
  downloadAttachment: async (slug: string, ref: string, filename: string) => {
    const { triggerBlobDownload } = await import("./api-workflows");
    triggerBlobDownload(await portalApi.attachmentBlob(slug, ref), filename || "attachment");
  },
  /** Fetch an app attachment's BYTES (member-gated) as a Blob — for an INLINE
   *  preview (`file_viewer`: PDF in an `<iframe>`, images in `<img>`). Mirrors
   *  `downloadAttachment` but RETURNS the blob instead of triggering a save, so
   *  the caller can `URL.createObjectURL` it (the auth'd URL can't ride an
   *  `<img src>`/`<iframe src>` — it needs the Bearer header). */
  attachmentBlob: async (slug: string, ref: string): Promise<Blob> => {
    const token = usePortalAuth.getState().token;
    const res = await fetch(
      new URL(
        `${BASE}/apps/${slug}/attachments?ref=${encodeURIComponent(ref)}`,
        window.location.origin,
      ),
      { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    );
    if (!res.ok) {
      if (res.status === 401) usePortalAuth.getState().logout();
      throw new ApiError(res.status, res.statusText, "download_failed");
    }
    return res.blob();
  },

  submitForm: (slug: string, formSlug: string, data: Record<string, unknown>) =>
    request<{ row: Record<string, unknown>; submit_message: string | null }>(
      `/apps/${slug}/forms/${formSlug}/submit`,
      { method: "POST", body: JSON.stringify({ data }) },
    ),

  // Public brand & layout + nav (ADR-0010 P7 B+C) — read before auth.
  getBranding: (slug: string) => request<PortalBranding>(`/apps/${slug}/branding`),
  getNav: (slug: string) => request<PortalNavItem[]>(`/apps/${slug}/nav`),

  /** The app's assignable members (ADR-0020 P3b) — for a `member` column
   *  picker. Member-scoped server-side (`app_users` is global). */
  listMembers: (slug: string) =>
    request<{ id: string; name: string; email: string }[]>(`/apps/${slug}/members`),

  // --- Notifications inbox (ADR-0020 P3) — apps-plane, member-gated --------
  listNotifications: (
    slug: string,
    opts: { limit?: number; offset?: number; unreadOnly?: boolean } = {},
  ) => {
    const p = new URLSearchParams();
    if (opts.limit != null) p.set("limit", String(opts.limit));
    if (opts.offset != null) p.set("offset", String(opts.offset));
    if (opts.unreadOnly) p.set("unread_only", "true");
    const qs = p.toString();
    return request<PortalNotificationsPage>(`/apps/${slug}/notifications${qs ? `?${qs}` : ""}`);
  },
  notificationsUnreadCount: (slug: string) =>
    request<{ unread: number }>(`/apps/${slug}/notifications/unread-count`),
  markNotificationRead: (slug: string, id: string) =>
    request<PortalNotification>(`/apps/${slug}/notifications/${id}/read`, { method: "POST" }),
  markAllNotificationsRead: (slug: string) =>
    request<{ marked: number }>(`/apps/${slug}/notifications/read-all`, { method: "POST" }),

  // --- Assistant conversation threads (ADR-0028 server-side memory) --------
  // Named, reopenable chat/voice threads, owner-scoped. The thread keeps the
  // transcript so it survives a page refresh (no more "everything vanishes").
  listAssistantSessions: (slug: string, surface: "chat" | "voice" = "chat") =>
    request<AssistantSessionSummary[]>(`/apps/${slug}/assistant/sessions?surface=${surface}`),
  getAssistantMessages: (slug: string, id: string) =>
    request<AssistantSessionMessage[]>(`/apps/${slug}/assistant/sessions/${id}`),
  createAssistantSession: (slug: string, surface: "chat" | "voice" = "chat") =>
    request<{ id: string }>(`/apps/${slug}/assistant/sessions`, {
      method: "POST",
      body: JSON.stringify({ surface }),
    }),
  deleteAssistantSession: (slug: string, id: string) =>
    request<void>(`/apps/${slug}/assistant/sessions/${id}`, { method: "DELETE" }),
  /** Switch which BRANCH the thread is on (ADR-0072) — pass ANY node of the branch you
   *  want (the server descends to its tip). A pointer move: nothing is copied or
   *  deleted, so switching back restores the other branch verbatim. */
  setAssistantActiveLeaf: (slug: string, id: string, messageId: string) =>
    request<void>(`/apps/${slug}/assistant/sessions/${id}/active-leaf`, {
      method: "PATCH",
      body: JSON.stringify({ message_id: messageId }),
    }),
  // Files the user attaches to a thread's sandbox workspace (ADR-0033 file-uploads).
  // The assistant reads them with its `read_uploaded_file` / `code_execution` tools.
  // Multipart upload (field `files`); the GET rebuilds the chips on reopen.
  uploadAssistantFiles: async (
    slug: string,
    threadId: string,
    files: File[],
  ): Promise<{ thread_id: string; uploaded: { name: string; size: number }[] }> => {
    const token = usePortalAuth.getState().token;
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    const res = await fetch(
      new URL(`${BASE}/apps/${slug}/assistant/sessions/${threadId}/files`, window.location.origin),
      { method: "POST", body: fd, headers: token ? { Authorization: `Bearer ${token}` } : {} },
    );
    if (!res.ok) {
      if (res.status === 401) usePortalAuth.getState().logout();
      throw new ApiError(res.status, res.statusText, "upload_failed");
    }
    return res.json() as Promise<{ thread_id: string; uploaded: { name: string; size: number }[] }>;
  },
  /** Fetch a produced file's BYTES (authenticated) as a Blob — for an INLINE preview
   *  (an image the assistant generated, e.g. a QR). `downloadAssistantFile` reuses it. */
  assistantFileBlob: async (slug: string, threadId: string, path: string): Promise<Blob> => {
    const token = usePortalAuth.getState().token;
    const enc = path.split("/").map(encodeURIComponent).join("/");
    const res = await fetch(
      new URL(
        `${BASE}/apps/${slug}/assistant/sessions/${threadId}/files/${enc}`,
        window.location.origin,
      ),
      { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    );
    if (!res.ok) {
      if (res.status === 401) usePortalAuth.getState().logout();
      throw new ApiError(res.status, res.statusText, "download_failed");
    }
    return res.blob();
  },
  /** Download a file PRODUCED by `code_execution` in the thread's sandbox workspace
   *  (e.g. a .pptx the assistant built). Authenticated fetch → blob → save-as. */
  downloadAssistantFile: async (slug: string, threadId: string, path: string) => {
    const blob = await portalApi.assistantFileBlob(slug, threadId, path);
    const { triggerBlobDownload } = await import("./api-workflows");
    triggerBlobDownload(blob, path.split("/").pop() || "file");
  },
  /** Fetch a chat-produced file's BYTES by its SIGNED, per-user token (the immutable-blob
   *  download seam). The token binds the blob to THIS app_user, so a historical download
   *  stays the right file even after the workspace reused the same filename. */
  assistantFileBlobByToken: async (slug: string, token: string): Promise<Blob> => {
    const authToken = usePortalAuth.getState().token;
    const res = await fetch(
      new URL(
        `${BASE}/apps/${slug}/assistant/files/blob?token=${encodeURIComponent(token)}`,
        window.location.origin,
      ),
      { headers: authToken ? { Authorization: `Bearer ${authToken}` } : {} },
    );
    if (!res.ok) {
      if (res.status === 401) usePortalAuth.getState().logout();
      throw new ApiError(res.status, res.statusText, "download_failed");
    }
    return res.blob();
  },
  /** Download a chat-produced file by its signed token → save-as (the blob-immutable path). */
  downloadAssistantFileByToken: async (slug: string, token: string, name: string) => {
    const blob = await portalApi.assistantFileBlobByToken(slug, token);
    const { triggerBlobDownload } = await import("./api-workflows");
    triggerBlobDownload(blob, name || "file");
  },
  listAssistantFiles: (slug: string, threadId: string) =>
    request<{ files: { name: string; size?: number }[] }>(
      `/apps/${slug}/assistant/sessions/${threadId}/files`,
    ),
  /** The resolved model + its native-input modalities for this app's assistant surface
   *  (ADR-0034) — drives the chat's "cosa vede il modello" badge. */
  assistantCapabilities: (slug: string, surface: "chat" | "voice" = "chat") =>
    request<PortalAssistantCapabilities>(`/apps/${slug}/assistant/capabilities?surface=${surface}`),
  /** Suggested opening questions for the empty chat, grounded in the surfaces the caller's
   *  role can read — the FE renders them as clickable starter chips (Wave-1 operator UX). */
  assistantStarters: (slug: string, surface: "chat" | "voice" = "chat") =>
    request<{ starters: PortalAssistantStarter[] }>(
      `/apps/${slug}/assistant/starters?surface=${surface}`,
    ),

  // --- Parent portal ("one login, many apps", ADR-0015) -----------------
  // Slug-less: the tenant brand + a membership-agnostic login + the launcher
  // feed. The token they mint is the SAME global portal JWT (works against
  // every app the user is a member of).
  /** PUBLIC tenant brand — read pre-auth by the parent login + launcher. */
  getParentBrand: () => request<ParentPortalBranding>(`/brand`),
  /** Membership-agnostic login (zero memberships → empty launcher). */
  parentLogin: (body: { email: string; password: string }) =>
    request<ParentAuthResult>(`/auth/login`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  /** Account-only self-signup (ADR-0016) — the single registration door.
   *  `direct` returns a token + user; `requires_approval` returns status
   *  only (no token), the account is pending until an admin accepts it. */
  parentSignup: (body: { email: string; password: string; full_name?: string }) =>
    request<PortalSignupResult>(`/auth/signup`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  /** Second login step (ADR-0017): exchange the challenge token + a TOTP /
   *  recovery code for the real session. */
  parentLoginTotp: (body: { challenge_token: string; code: string }) =>
    request<ParentAuthResult>(`/auth/login/totp`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // --- 2FA self-service (ADR-0017) --------------------------------------
  totpStatus: () => request<{ enabled: boolean; recovery_codes_remaining: number }>(`/auth/2fa`),
  /** `setupToken` is the forced-enrollment token from a `totp_setup_required`
   *  login (no session yet); omit it for voluntary enrollment (uses session). */
  totpBegin: (setupToken?: string) =>
    request<{ secret: string; otpauth_uri: string }>(`/auth/2fa/begin`, {
      method: "POST",
      ...(setupToken ? { headers: { Authorization: `Bearer ${setupToken}` } } : {}),
    }),
  totpConfirm: (code: string, setupToken?: string) =>
    request<{ recovery_codes: string[]; access_token: string }>(`/auth/2fa/confirm`, {
      method: "POST",
      body: JSON.stringify({ code }),
      ...(setupToken ? { headers: { Authorization: `Bearer ${setupToken}` } } : {}),
    }),
  totpDisable: (code: string) =>
    request<void>(`/auth/2fa/disable`, { method: "POST", body: JSON.stringify({ code }) }),

  /** Current parent session (global identity, no app context). */
  parentMe: () => request<PortalUser>(`/auth/me`),
  /** Self-service profile edit (account page) — name / email. */
  updateProfile: (body: { full_name?: string | null; email?: string }) =>
    request<PortalUser>(`/auth/me`, { method: "PATCH", body: JSON.stringify(body) }),
  /** Self-service password change (current-password gated). */
  changePassword: (body: { current_password: string; new_password: string }) =>
    request<void>(`/auth/change-password`, { method: "POST", body: JSON.stringify(body) }),
  /** Logout (204) — token is client-held, so this is a courtesy call. */
  // Voluntary logout: send the refresh token so the server retires its family
  // (parity with the operator realm). The 401 handlers use the bare store `logout()`
  // — a dead session has nothing to revoke.
  parentLogout: () => {
    const { refreshToken } = usePortalAuth.getState();
    return request<void>(`/auth/logout`, {
      method: "POST",
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
  },
  /** The launcher feed: the apps this user can open + role + branding. */
  myApps: () => request<MyAppOut[]>(`/me/apps`),

  /** The signed-in member's EDITABLE profile fields + values in one app
   *  (access-model). Locked fields are operator-only and never returned. */
  getMyProfile: (slug: string) => request<PortalMemberProfile>(`/apps/${slug}/auth/profile`),
  /** Save the editable attributes (locked keys are rejected server-side). */
  updateMyProfile: (slug: string, attrs: Record<string, unknown>) =>
    request<PortalMemberProfile>(`/apps/${slug}/auth/profile`, {
      method: "PATCH",
      body: JSON.stringify({ attrs }),
    }),

  // Self-serve personal API keys (ADR-0052 S4) — a token that acts AS the member
  // in ONE app, its data boundary the member's OWN role RLS. Self-serve only.
  listAppKeys: (slug: string) => request<PortalAppKey[]>(`/apps/${slug}/account/api-keys`),
  createAppKey: (slug: string, name?: string) =>
    request<PortalAppKeyCreated>(`/apps/${slug}/account/api-keys`, {
      method: "POST",
      body: JSON.stringify({ name: name || null }),
    }),
  revokeAppKey: (slug: string, keyId: string) =>
    request<void>(`/apps/${slug}/account/api-keys/${keyId}`, { method: "DELETE" }),

  // App-admin service accounts (ADR-0052 S4b) — a `can_admin`-role member provisions
  // app-scoped service identities for THEIR app (confined; permissions apps-only).
  listAppServiceAccounts: (slug: string) =>
    request<PortalServiceAccount[]>(`/apps/${slug}/service-accounts`),
  listAppServiceAccountRoles: (slug: string) =>
    request<PortalAppRole[]>(`/apps/${slug}/service-accounts/roles`),
  createAppServiceAccount: (slug: string, body: PortalServiceAccountInput) =>
    request<PortalServiceAccount>(`/apps/${slug}/service-accounts`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateAppServiceAccount: (slug: string, saId: string, body: PortalServiceAccountInput) =>
    request<PortalServiceAccount>(`/apps/${slug}/service-accounts/${saId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  removeAppServiceAccount: (slug: string, saId: string) =>
    request<void>(`/apps/${slug}/service-accounts/${saId}`, { method: "DELETE" }),
  listAppServiceAccountKeys: (slug: string, saId: string) =>
    request<PortalAppKey[]>(`/apps/${slug}/service-accounts/${saId}/keys`),
  mintAppServiceAccountKey: (slug: string, saId: string, name?: string) =>
    request<PortalAppKeyCreated>(`/apps/${slug}/service-accounts/${saId}/keys`, {
      method: "POST",
      body: JSON.stringify({ name: name || null }),
    }),
  revokeAppServiceAccountKey: (slug: string, saId: string, keyId: string) =>
    request<void>(`/apps/${slug}/service-accounts/${saId}/keys/${keyId}`, { method: "DELETE" }),

  // Per-user portal UI preferences (G10) — durable, cross-device store for the
  // data-grid column layout + saved views. Login-gated.
  getPreferences: (slug: string) =>
    request<{ preferences: Record<string, unknown> }>(`/apps/${slug}/auth/preferences`),
  patchPreferences: (slug: string, patch: Record<string, unknown>) =>
    request<{ preferences: Record<string, unknown> }>(`/apps/${slug}/auth/preferences`, {
      method: "PATCH",
      body: JSON.stringify({ patch }),
    }),
};

/** Public brand & layout config for an app's portal (mirrors backend
 *  `PortalBranding` / `apps.portal_config`). */
export interface PortalBranding {
  app_name: string;
  layout: string;
  /** Free-form slot → colour map; slots are layout-defined (lib/portalLayouts). */
  theme: Record<string, string>;
  logo_url: string | null;
  login: { title: string | null; subtitle: string | null };
  show_nav: boolean;
  /** Default page content width: "contained" | "full" (S8b). */
  content_width?: string;
  /** Slug of the app's HOME page (the `is_home` page; its slug is arbitrary).
   *  The SPA entry route resolves the real home from here. */
  home_slug?: string | null;
  /** Whether the app's enabled `voice` interface is EMBEDDED (ADR-0049) — drives
   *  the portal's voice-assistant widget/launcher. An interface with
   *  `embedded=false` (standalone-only) does NOT mount the widget. */
  voice_enabled?: boolean;
  /** Which speech edges to use (ADR-0025): "server" when the self-hosted voice
   *  sidecar is configured (Whisper/Piper), else "browser" (Web Speech). */
  voice_stt?: "server" | "browser";
  voice_tts?: "server" | "browser";
  /** Whether the voice interface runs the in-browser wake word ("hey jarvis")
   *  for hands-free activation (ADR-0025). */
  voice_wake_word?: boolean;
  /** Whether the app's enabled `chat` interface is EMBEDDED (ADR-0028 Block E /
   *  ADR-0049) — drives the portal's text-chat widget/launcher. Same brain +
   *  scope as voice. */
  chat_enabled?: boolean;
  /** ADR-0049 exposure model — the enabled interface has its OWN standalone
   *  front-door surface (`standalone=true`). Orthogonal to `*_enabled`
   *  (the embedded widget): drives the home resolver + the standalone nav link. */
  voice_standalone?: boolean;
  chat_standalone?: boolean;
}

/** PUBLIC tenant brand for the PARENT portal (ADR-0015) — the brand above all
 *  apps, read pre-auth by the launcher + the slug-less login. */
export interface ParentPortalBranding {
  name: string | null;
  logo_url: string | null;
  theme: Record<string, string>;
  login: { title: string | null; subtitle: string | null };
  /** Operator master-switch for the in-app waffle app-switcher. */
  switcher_enabled: boolean;
  /** Optional small footer line shown on the login + launcher. */
  footer?: string | null;
  /** Whether public self-signup is open (ADR-0016) — login shows "create
   *  account" when true. */
  signup_enabled?: boolean;
}

/** Result of a parent (slug-less) login — NO role (membership-agnostic).
 *  Polymorphic by `status` (ADR-0017): `ok` carries a session; `totp_required`
 *  a challenge token; `totp_setup_required` a forced-enrollment token. */
export interface ParentAuthResult {
  status?: "ok" | "totp_required" | "totp_setup_required";
  access_token: string | null;
  /** Rotated refresh token — renews the access token silently (aligned realms). */
  refresh_token?: string | null;
  token_type: string;
  user: PortalUser | null;
  challenge_token?: string | null;
  setup_token?: string | null;
}

/** Result of a parent self-signup (ADR-0016). `direct` → active + token;
 *  `requires_approval` → pending, no token. */
export interface PortalSignupResult {
  status: "active" | "pending";
  access_token?: string | null;
  refresh_token?: string | null;
  token_type?: string;
  user?: PortalUser | null;
}

/** One app the signed-in end-user can open — a launcher tile (ADR-0015). */
export interface MyAppOut {
  slug: string;
  name: string;
  role: string;
  /** Whether the member's role is app-admin here (ADR-0052 S4b) — drives the
   *  portal service-account provisioning surface. */
  can_admin?: boolean;
  logo_url: string | null;
  theme: Record<string, string>;
}

/** One EDITABLE per-app profile field the member may self-set (access-model). */
export interface PortalMemberProfileField {
  key: string;
  label: string;
  data_type: "text" | "number" | "select";
  options: string[] | null;
}

export interface PortalMemberProfile {
  fields: PortalMemberProfileField[];
  values: Record<string, unknown>;
}

/** A member's personal API key for one app (ADR-0052 S4). */
export interface PortalAppKey {
  key_id: string;
  name: string | null;
  last_used_at: string | null;
  expires_at: string | null;
  revoked_at: string | null;
  created_at: string;
}

export interface PortalAppKeyCreated extends PortalAppKey {
  /** The plaintext secret — shown ONCE at creation. */
  value: string;
}

/** An app-scoped service account provisioned by an app-admin (ADR-0052 S4b). */
export interface PortalServiceAccount {
  id: string;
  name: string;
  description: string | null;
  /** ADR-0053 S6: the app role this SA acts AS (its /svc boundary). */
  role_id: string | null;
  role_name: string | null;
  enabled: boolean;
  created_at: string;
}

export interface PortalServiceAccountInput {
  name?: string;
  description?: string | null;
  /** ADR-0053 S6: a role of THIS app — required on create. */
  role_id?: string;
  enabled?: boolean;
}

export interface PortalAppRole {
  id: string;
  name: string;
}

/**
 * One resolved entry in the portal nav sidebar (improvement #1). A node is one
 * of three kinds:
 *  - `page`  — internal link to an app page (`slug` + `is_home`/`access`).
 *  - `link`  — external `url` (opens in a new tab).
 *  - `group` — a labelled header with `children` (page/link only, one level).
 * Unconfigured apps emit a flat list of `page` nodes (the auto behaviour).
 */
export interface PortalNavItem {
  kind: "page" | "link" | "group";
  label: string;
  slug?: string | null; // kind=page
  url?: string | null; // kind=link
  icon?: string | null; // optional lucide icon name
  is_home?: boolean; // kind=page
  access?: string | null; // kind=page: public | restricted
  children?: PortalNavItem[]; // kind=group
}
